<template>
    <div class="w-full h-full relative">
        <Header :title="'Meine Dokumente'" />

        <!-- Upload Button -->
        <custom-button
            class="w-full sm:w-80 h-11 mt-4 mb-8 px-4"
            :text="'Datein hochladen'"
            :bgColor="'bg-primary'"
            :textColor="'text-white'"
            :id="'upload-button'"
            @click="$refs.fileInput.click()"
        >
            <template #icon>
                <upload-icon
                    :fillClass="'fill-white'"
                    class="mr-4 w-5"
                />
            </template>
        </custom-button>
        <input
            type="file"
            ref="fileInput"
            hidden
            @change="UPLOAD_COURSE_WORKBOOK"
        />

        <content-container class="content-container-big overflow-hidden" style="max-height: calc(100vh - 280px);">
            <div class="w-full px-2 sm:px-0" v-if="GET_MATERIALS.length == 0">
                <div
                    class="text-base sm:text-lg text-primary font-semibold mb-7 whitespace-pre-wrap text-center sm:text-left"
                >
                    Schließe Deine erste Ausbildung ab, um hier Zugriff zu all
                    deinen Dokumenten zu erhalten.
                </div>
                <custom-button
                    class="w-full xs:w-64 h-11"
                    :text="'Jetzt durchstarten'"
                    :bgColor="'bg-primary'"
                    :textColor="'text-white'"
                    :id="'change-page-button'"
                    @click="$router.push({ name: 'Dashboard' })"
                />
            </div>

            <!-- Tab Navigation -->
            <div v-if="GET_MATERIALS.length > 0" class="w-full h-full flex flex-col">
                <div class="border-b border-gray-200 mb-4 sm:mb-6 flex-shrink-0 -mx-2 sm:mx-0">
                    <nav class="flex space-x-0 sm:space-x-8 overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 px-2 sm:px-0">
                        <button
                            v-for="(category, index) in sortedMaterials"
                            :key="index"
                            @click="setActiveTab(index)"
                            :class="[
                                'py-3 px-3 sm:px-1 border-b-2 font-medium text-xs sm:text-sm transition-colors duration-200 whitespace-nowrap flex-shrink-0 min-w-0',
                                state.activeTab === index
                                    ? 'border-primary text-primary'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            ]"
                        >
                            <span class="truncate max-w-[120px] sm:max-w-none block">
                                {{ category.name || 'Allgemeine Dokumente' }}
                            </span>
                        </button>
                    </nav>
                </div>

                <!-- Tab Content -->
                <div class="w-full flex-1 overflow-y-auto -mx-2 sm:mx-0 px-2 sm:px-0 pb-20 sm:pb-4">
                    <documents-container
                        v-for="(category, index) in sortedMaterials"
                        :key="index"
                        v-show="state.activeTab === index"
                        :category="{ 
                            ...category, 
                            name: category.name || 'Allgemeine Dokumente' 
                        }"
                        @open-media="openMedia"
                    />
                </div>
            </div>
        </content-container>
    </div>
</template>

<script>
import { computed, defineComponent, reactive } from "@vue/runtime-core";
import { useStore } from "vuex";
import CustomButton from "../../components/input/CustomButton.vue";
import ContentContainer from "../../components/layout/ContentContainer.vue";
import Header from "../../components/layout/Header.vue";
import { getData } from "../../common/dataHelper";
import { MEDIA_TYPES, HELPER_TYPES, STORE_TYPES, COURSE_TYPES } from "../../store/types";
import DocumentsContainer from "./DocumentsContainer.vue";

export default defineComponent({
    name: "Documents",
    components: { Header, CustomButton, DocumentsContainer, ContentContainer },
    emits: ["openMedia"],
    setup(_1, ctx) {
        const store = useStore();
        
        /**
         *  State
         */
        const state = reactive({
            activeTab: 0
        });
        
        /**
         *  Functions
         */

        async function UPLOAD_COURSE_WORKBOOK(event) {
            const files = event.target.files;
            if (!files) {
                return;
            }
            const file = files[0];
            store.commit(
                `${STORE_TYPES.HELPERS}/${HELPER_TYPES.SET_LOADING_ID}`,
                "upload-button"
            );
            const response = await store.dispatch(
                `${STORE_TYPES.COURSES}/${COURSE_TYPES.UPLOAD_DOCUMENT}`,
                {
                    file,
                }
            );
            if (getData(response, "status") != 201) {
                store.commit(
                    `${STORE_TYPES.HELPERS}/${HELPER_TYPES.SET_TOAST_MESSAGE}`,
                    "Fehler beim Upload."
                );
                
            }
            else {
                // Erfolgsmeldung hinzufügen
                store.commit(
                    `${STORE_TYPES.HELPERS}/${HELPER_TYPES.SET_TOAST_MESSAGE}`,
                    "Datei erfolgreich hochgeladen."
                );
            }
            event.target.files = null;

            store.commit(
                `${STORE_TYPES.HELPERS}/${HELPER_TYPES.REMOVE_LOADING_ID}`,
                "upload-button"
            );
        }

        async function FETCH_MATERIALS() {
            return store.dispatch(
                `${STORE_TYPES.MEDIA}/${MEDIA_TYPES.FETCH_MATERIALS}`
            );
        }

        function setActiveTab(index) {
            state.activeTab = index;
        }

        /**
         *  Computed
         */
        const GET_MATERIALS = computed(() => {
            return store.getters[
                `${STORE_TYPES.MEDIA}/${MEDIA_TYPES.GET_MATERIALS}`
            ];
        });

        const sortedMaterials = computed(() => {
            const materials = GET_MATERIALS.value;
            if (!materials || materials.length === 0) return [];
            
            // Sortiere so dass "Allgemeine Dokumente" (name ist null/undefined) zuerst kommt
            return [...materials].sort((a, b) => {
                const aIsGeneral = !a.name || a.name === 'Allgemeine Dokumente';
                const bIsGeneral = !b.name || b.name === 'Allgemeine Dokumente';
                
                if (aIsGeneral && !bIsGeneral) return -1;
                if (!aIsGeneral && bIsGeneral) return 1;
                
                // Beide sind entweder allgemein oder spezifisch - sortiere alphabetisch
                const aName = a.name || 'Allgemeine Dokumente';
                const bName = b.name || 'Allgemeine Dokumente';
                return aName.localeCompare(bName);
            });
        });

        /**
         *  Created
         */

        store.commit(
            `${STORE_TYPES.HELPERS}/${HELPER_TYPES.SET_LOADING_ID}`,
            "isLoadingData"
        );
        Promise.all([FETCH_MATERIALS()]).then(() => {
            store.commit(
                `${STORE_TYPES.HELPERS}/${HELPER_TYPES.REMOVE_LOADING_ID}`,
                "isLoadingData"
            );
        });

        function openMedia(media) {
            ctx.emit("openMedia", media);
        }

        return { 
            state, 
            GET_MATERIALS, 
            sortedMaterials,
            openMedia, 
            UPLOAD_COURSE_WORKBOOK,
            setActiveTab
        };
    },
});
</script>